import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  BellIcon,
  CheckIcon,
  CheckCircleIcon,
  TrashIcon,
  FunnelIcon,
  ArchiveBoxIcon,
  MegaphoneIcon,
  CalendarIcon,
  AcademicCapIcon,
  TruckIcon,
  BanknotesIcon,
  BookOpenIcon,
  CogIcon,
  UserGroupIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline'
import { useNotifications } from '@/store/NotificationContext'
import { formatDistanceToNow, format, parseISO } from 'date-fns'
import type { NotificationType } from '@/api/communicationApi'
import { Button } from '@/components/ui/Button'

const notificationTypeConfig: Record<NotificationType, { icon: typeof MegaphoneIcon; label: string; color: string; bgColor: string }> = {
  announcement: { icon: MegaphoneIcon, label: 'Announcement', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  event_reminder: { icon: CalendarIcon, label: 'Event Reminder', color: 'text-purple-600', bgColor: 'bg-purple-100' },
  attendance_alert: { icon: UserGroupIcon, label: 'Attendance Alert', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  transport_update: { icon: TruckIcon, label: 'Transport Update', color: 'text-green-600', bgColor: 'bg-green-100' },
  academic_update: { icon: AcademicCapIcon, label: 'Academic Update', color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  fee_reminder: { icon: BanknotesIcon, label: 'Fee Reminder', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  library_notice: { icon: BookOpenIcon, label: 'Library Notice', color: 'text-teal-600', bgColor: 'bg-teal-100' },
  system: { icon: CogIcon, label: 'System', color: 'text-gray-600', bgColor: 'bg-gray-100' }
}

export default function NotificationsPage() {
  const navigate = useNavigate()
  const {
    notifications,
    counts,
    isLoading,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
    hasMore,
    currentPage
  } = useNotifications()

  const [statusFilter, setStatusFilter] = useState<'all' | 'unread' | 'read'>('all')
  const [typeFilter, setTypeFilter] = useState<NotificationType | ''>('')

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    if (statusFilter === 'unread' && notification.status !== 'unread') return false
    if (statusFilter === 'read' && notification.status !== 'read') return false
    if (typeFilter && notification.sourceType !== typeFilter) return false
    return true
  })

  const handleNotificationClick = async (notification: typeof notifications[0]) => {
    if (notification.status === 'unread') {
      await markAsRead(notification.id)
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl)
    }
  }

  const handleLoadMore = () => {
    if (hasMore && !isLoading) {
      fetchNotifications(currentPage + 1)
    }
  }

  const handleMarkAllRead = async () => {
    await markAllAsRead()
  }

  const getTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return 'Just now'
    }
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email': return EnvelopeIcon
      case 'sms': return DevicePhoneMobileIcon
      default: return ComputerDesktopIcon
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500 mt-1">
            Stay updated with important announcements and alerts
          </p>
        </div>
        {counts.unread > 0 && (
          <Button
            onClick={handleMarkAllRead}
            variant="secondary"
            className="mt-4 sm:mt-0"
          >
            <CheckCircleIcon className="h-5 w-5 mr-2" />
            Mark all as read ({counts.unread})
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BellIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
              <p className="text-xs text-gray-500">Total Notifications</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <BellIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.unread}</p>
              <p className="text-xs text-gray-500">Unread</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{counts.total - counts.unread}</p>
              <p className="text-xs text-gray-500">Read</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('unread')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === 'unread' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Unread
              </button>
              <button
                onClick={() => setStatusFilter('read')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === 'read' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Read
              </button>
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as NotificationType | '')}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">All Types</option>
              {Object.entries(notificationTypeConfig).map(([type, config]) => (
                <option key={type} value={type}>{config.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading && notifications.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Loading notifications...</p>
            </div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <BellIcon className="h-12 w-12 text-gray-300" />
            <p className="mt-4 text-gray-500">No notifications found</p>
            <p className="text-sm text-gray-400 mt-1">
              {statusFilter === 'unread' ? 'All caught up!' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification, index) => {
              const config = notificationTypeConfig[notification.sourceType] || notificationTypeConfig.system
              const IconComponent = config.icon
              const ChannelIcon = getChannelIcon(notification.channel)

              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                    notification.status === 'unread'
                      ? 'border-primary-500 bg-primary-50/30'
                      : 'border-transparent'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 p-2 rounded-lg ${config.bgColor}`}>
                      <IconComponent className={`h-5 w-5 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className={`text-sm font-medium ${
                            notification.status === 'unread' ? 'text-gray-900' : 'text-gray-600'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        {notification.status === 'unread' && (
                          <span className="ml-2 flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full" />
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-3 text-xs text-gray-400">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full ${config.bgColor} ${config.color}`}>
                            {config.label}
                          </span>
                          <span className="flex items-center">
                            <ChannelIcon className="h-3 w-3 mr-1" />
                            {notification.channel}
                          </span>
                          <span>{getTimeAgo(notification.createdAt)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {notification.priority === 'urgent' && (
                            <span className="text-xs font-medium text-red-600 bg-red-100 px-1.5 py-0.5 rounded">
                              Urgent
                            </span>
                          )}
                          {notification.priority === 'high' && (
                            <span className="text-xs font-medium text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                              High
                            </span>
                          )}
                          {notification.status === 'unread' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                markAsRead(notification.id)
                              }}
                              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              title="Mark as read"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}

        {/* Load More */}
        {hasMore && filteredNotifications.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isLoading}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              {isLoading ? 'Loading...' : 'Load more notifications'}
            </button>
          </div>
        )}
      </div>

      {/* Notification Types Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Notifications by Type</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(counts.byType).map(([type, count]) => {
            const config = notificationTypeConfig[type as NotificationType]
            if (!config) return null
            const IconComponent = config.icon

            return (
              <button
                key={type}
                onClick={() => setTypeFilter(type as NotificationType)}
                className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  typeFilter === type ? 'bg-primary-50 ring-2 ring-primary-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className={`p-2 rounded-lg ${config.bgColor}`}>
                  <IconComponent className={`h-4 w-4 ${config.color}`} />
                </div>
                <div className="text-left">
                  <p className="text-lg font-bold text-gray-900">{count}</p>
                  <p className="text-xs text-gray-500">{config.label}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
